/** Pure timeline maths: what moved, filtered and grouped by the day it happened. */
import { formatDay, movementDirection, stanceTime, subjectKey } from '../components/format';
import type { MovementEntry, SubjectRef } from '../../../types/issues';

export type DirectionFilter = 'all' | 'towards' | 'away';

export interface MovementDay {
  /** Start of that local day, in ms — stable key and sort value */
  key: number;
  label: string;
  entries: MovementEntry[];
}

export function filterMovements(
  entries: MovementEntry[],
  direction: DirectionFilter,
  subject: string | 'all',
): MovementEntry[] {
  return entries.filter((entry) => {
    if (subject !== 'all' && subjectKey(entry.stance.subject) !== subject) return false;
    if (direction === 'all') return true;
    // A first reading has no direction, so it belongs to neither side
    return movementDirection(entry) === direction;
  });
}

/** Newest day first, and newest movement first inside each day */
export function groupByDay(entries: MovementEntry[], now: number = Date.now()): MovementDay[] {
  const days = new Map<number, MovementEntry[]>();
  entries.forEach((entry) => {
    const at = new Date(stanceTime(entry.stance));
    at.setHours(0, 0, 0, 0);
    const key = at.getTime();
    const bucket = days.get(key);
    if (bucket) bucket.push(entry);
    else days.set(key, [entry]);
  });
  return Array.from(days.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([key, list]) => ({
      key,
      label: formatDay(new Date(key).toISOString(), now),
      entries: list.sort((a, b) => stanceTime(b.stance) - stanceTime(a.stance)),
    }));
}

/** Everyone who appears in the window, for the entity filter */
export function subjectsIn(entries: MovementEntry[]): SubjectRef[] {
  const seen = new Map<string, SubjectRef>();
  entries.forEach((entry) => {
    const key = subjectKey(entry.stance.subject);
    if (!seen.has(key)) seen.set(key, entry.stance.subject);
  });
  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function countDirections(entries: MovementEntry[]): { towards: number; away: number; first: number } {
  const counts = { towards: 0, away: 0, first: 0 };
  entries.forEach((entry) => {
    counts[movementDirection(entry)] += 1;
  });
  return counts;
}
